/**
 * `supply-chain` v1 — a layered flow graph.
 *
 * Nodes belong to an ordered stage and links only join consecutive stages, so
 * the graph is a DAG that reads left to right. Every value is in one unit and
 * flows are conserved: the values of the nodes of a stage add up to the same
 * total, and a node's value equals the sum of its links on each side. The source
 * bounds the size of the graph: what falls outside its limits is merged into an
 * `Other` node of the stage (id ending in `:other`).
 */
export const SUPPLY_CHAIN_CONTRACT = {
  name: "supply-chain",
  version: 1,
} as const;

/** Known stages, in flow order. A stage the source adds later is placed after these. */
export const SUPPLY_CHAIN_STAGES = [
  "PIRATE_SERVICE",
  "WATERMARK_ID",
  "CDN_PROVIDER",
  "PIRATE_HOST",
  "ACCOUNT",
  "OUTCOME",
] as const;

export type SupplyChainStage = (typeof SUPPLY_CHAIN_STAGES)[number] | (string & {});

export interface SupplyChainNode {
  id: string;
  label: string;
  stage: SupplyChainStage;
  value: number;
  /**
   * How many distinct entities the node stands for: 1, or more for an `Other`
   * node that merges what falls beyond the source's limit. Optional: a source
   * that omits it counts every node as one entity.
   */
  members?: number;
}

export interface SupplyChainLink {
  source: string;
  target: string;
  value: number;
}

export interface SupplyChainFreshness {
  generatedAt: string;
  dataThrough: string;
  /** "demo", "clickhouse", or a named upstream feed. */
  source: string;
  isEstimated: boolean;
}

export interface SupplyChainData {
  nodes: SupplyChainNode[];
  links: SupplyChainLink[];
  freshness: SupplyChainFreshness | null;
}
